import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Clock, Package, Link2, AlertCircle, CheckCircle2, RefreshCw, CheckCircle, Loader2, RotateCcw } from 'lucide-react';
import { api } from '../../lib/api';

type Tab = 'activity' | 'jobs';

// ─── Activity log ────────────────────────────────────────────────────────────

const ACTION_META: Record<string, { icon: any; colour: string; label: string }> = {
  created:           { icon: Package,       colour: '#7c5cfc', label: 'Listing created' },
  updated:           { icon: Package,       colour: '#7c5cfc', label: 'Listing updated' },
  deleted:           { icon: Package,       colour: '#f97316', label: 'Listing deleted' },
  published:         { icon: CheckCircle2,  colour: '#2de8c8', label: 'Published' },
  publish_failed:    { icon: AlertCircle,   colour: '#f97316', label: 'Publish failed' },
  delisted:          { icon: Package,       colour: '#a78bfa', label: 'Delisted' },
  crosslist_queued:  { icon: RefreshCw,     colour: '#7c5cfc', label: 'Crosslist queued' },
  bulk_updated:      { icon: Package,       colour: '#a78bfa', label: 'Bulk update' },
  connected:         { icon: Link2,         colour: '#2de8c8', label: 'Platform connected' },
  disconnected:      { icon: Link2,         colour: '#f97316', label: 'Platform disconnected' },
  signed_up:         { icon: CheckCircle2,  colour: '#2de8c8', label: 'Account created' },
  logged_in:         { icon: Clock,         colour: '#a78bfa', label: 'Logged in' },
};

function formatTime(iso: string): string {
  const date = new Date(iso);
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d ago`;
  return date.toLocaleDateString();
}

// ─── Jobs ────────────────────────────────────────────────────────────────────

interface Job {
  id: string; type: string;
  status: 'queued' | 'running' | 'completed' | 'failed';
  progress: number; totalSteps: number; completedSteps: number;
  error: string | null; createdAt: string; startedAt: string | null; completedAt: string | null;
  listing: { id: string; title: string } | null;
}

function relativeTime(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

const STATUS_CONFIG = {
  queued:    { label: 'Queued',  icon: Clock,         colour: 'text-white/40 bg-white/[0.06] border-white/[0.08]' },
  running:   { label: 'Running', icon: Loader2,       colour: 'text-primary-300 bg-primary-500/[0.1] border-primary-500/25' },
  completed: { label: 'Done',    icon: CheckCircle,   colour: 'text-success bg-success/[0.08] border-success/20' },
  failed:    { label: 'Failed',  icon: AlertCircle,   colour: 'text-danger bg-danger/[0.08] border-danger/20' },
} as const;

// ─── Main component ───────────────────────────────────────────────────────────

export function ActivityPage() {
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = (searchParams.get('tab') as Tab) ?? 'activity';

  function setTab(t: Tab) {
    setSearchParams({ tab: t }, { replace: true });
  }

  // Activity log
  const { data: logs = [], isLoading: logsLoading } = useQuery({
    queryKey: ['audit-log'],
    queryFn: () => api.get('/audit', { params: { limit: 200 } }).then((r) => r.data),
    refetchInterval: 30_000,
    enabled: tab === 'activity',
  });

  // Jobs
  const { data: jobs = [], isFetching: jobsFetching } = useQuery<Job[]>({
    queryKey: ['jobs'],
    queryFn: () => api.get('/jobs').then((r) => r.data),
    refetchInterval: (query) => {
      const data = query.state.data;
      return Array.isArray(data) && data.some((j) => j.status === 'queued' || j.status === 'running') ? 3000 : false;
    },
    enabled: tab === 'jobs',
  });

  const retry = useMutation({
    mutationFn: (jobId: string) => api.post(`/jobs/${jobId}/retry`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['jobs'] }),
  });

  // Mark activity as viewed when on activity tab
  useEffect(() => {
    if (tab !== 'activity') return;
    api.get('/auth/me').then((r) => {
      const userId = r.data?.user?.id;
      if (userId) {
        localStorage.setItem(`crosova_activity_viewed_${userId}`, new Date().toISOString());
        qc.invalidateQueries({ queryKey: ['audit-count'] });
      }
    }).catch(() => {});
  }, [tab, qc]);

  const activeJobs = jobs.filter((j) => j.status === 'queued' || j.status === 'running');
  const pastJobs   = jobs.filter((j) => j.status === 'completed' || j.status === 'failed');

  return (
    <div className="flex flex-col gap-3.5">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-base font-semibold tracking-tight">Activity</h1>
          <p className="text-[11.5px] text-white/25 mt-0.5">Logs and background jobs</p>
        </div>
        {tab === 'activity' && (
          <div className="text-[10px] text-white/30 flex items-center gap-1.5">
            <div className="w-1.5 h-1.5 bg-secondary-400 rounded-full animate-pulse" />
            Live
          </div>
        )}
        {tab === 'jobs' && (
          <button
            onClick={() => qc.invalidateQueries({ queryKey: ['jobs'] })}
            className="btn-secondary flex items-center gap-1.5 !py-1.5 !text-xs"
          >
            <RefreshCw size={12} className={jobsFetching ? 'animate-spin' : ''} />
            Refresh
          </button>
        )}
      </div>

      {/* Tab switcher */}
      <div className="flex gap-1 bg-white/[0.03] border border-white/[0.06] rounded-xl p-1">
        {(['activity', 'jobs'] as Tab[]).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`flex-1 py-2 rounded-lg text-[12.5px] font-medium transition-colors capitalize ${
              tab === t
                ? 'bg-primary-500/[0.15] text-primary-300'
                : 'text-white/40 hover:text-white/60'
            }`}
          >
            {t === 'activity' ? 'Activity log' : 'Jobs'}
          </button>
        ))}
      </div>

      {/* Activity log tab */}
      {tab === 'activity' && (
        <div className="card overflow-hidden">
          {logsLoading ? (
            <div className="p-10 text-center text-white/30 text-sm">Loading activity...</div>
          ) : logs.length === 0 ? (
            <div className="p-10 text-center text-white/30 text-sm">No activity yet</div>
          ) : (
            <div className="divide-y divide-white/[0.04]">
              {logs.map((log: any) => {
                const meta = ACTION_META[log.action] ?? { icon: Clock, colour: '#666', label: log.action };
                const Icon = meta.icon;
                return (
                  <div key={log.id} className="flex items-start gap-3 px-4 py-3 hover:bg-white/[0.02]">
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${meta.colour}15` }}>
                      <Icon size={14} style={{ color: meta.colour }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline justify-between gap-3">
                        <div className="text-[12.5px] text-white/85 font-medium">{meta.label}</div>
                        <div className="text-[10px] text-white/[0.22] flex-shrink-0">{formatTime(log.createdAt)}</div>
                      </div>
                      <div className="text-[11px] text-white/35 mt-0.5">
                        {log.entityType} · {log.entityId.slice(0, 12)}
                        {log.metadata?.platform && <span className="ml-2">on <strong className="text-white/55">{log.metadata.platform}</strong></span>}
                        {log.metadata?.error && <span className="ml-2 text-orange-400">— {log.metadata.error}</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Jobs tab */}
      {tab === 'jobs' && (
        <div className="flex flex-col gap-4">
          {activeJobs.length > 0 && (
            <section>
              <div className="text-[10px] font-semibold text-white/25 uppercase tracking-[0.8px] mb-2">Active ({activeJobs.length})</div>
              <div className="flex flex-col gap-2">
                {activeJobs.map((job) => <JobRow key={job.id} job={job} onRetry={() => retry.mutate(job.id)} />)}
              </div>
            </section>
          )}
          {pastJobs.length > 0 && (
            <section>
              <div className="text-[10px] font-semibold text-white/25 uppercase tracking-[0.8px] mb-2">History</div>
              <div className="flex flex-col gap-2">
                {pastJobs.map((job) => <JobRow key={job.id} job={job} onRetry={() => retry.mutate(job.id)} />)}
              </div>
            </section>
          )}
          {jobs.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-10 h-10 rounded-xl bg-white/[0.04] border border-white/[0.06] flex items-center justify-center mb-3">
                <Clock size={18} className="text-white/20" />
              </div>
              <p className="text-sm text-white/30">No jobs yet</p>
              <p className="text-xs text-white/20 mt-1">Jobs appear here when you crosslist or delist items</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function JobRow({ job, onRetry }: { job: Job; onRetry: () => void }) {
  const cfg = STATUS_CONFIG[job.status];
  const StatusIcon = cfg.icon;
  const isRunning = job.status === 'running';

  return (
    <div className="card p-3.5 flex items-center gap-3">
      <div className={`w-7 h-7 rounded-lg border flex items-center justify-center flex-shrink-0 ${cfg.colour}`}>
        <StatusIcon size={13} className={isRunning ? 'animate-spin' : ''} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-medium text-white/80 truncate">{job.listing?.title ?? `Job ${job.id.slice(-6)}`}</span>
          <span className={`text-[10px] font-medium px-1.5 py-px rounded border ${cfg.colour}`}>{cfg.label}</span>
          <span className="text-[10px] text-white/25 uppercase tracking-wide">{job.type}</span>
        </div>
        {(isRunning || job.status === 'queued') && (
          <div className="mt-1.5 flex items-center gap-2">
            <div className="flex-1 h-1 bg-white/[0.06] rounded-full overflow-hidden">
              <div className="h-full bg-primary-500 rounded-full transition-all duration-500" style={{ width: `${job.progress}%` }} />
            </div>
            <span className="text-[10px] text-white/30 flex-shrink-0">{job.completedSteps}/{job.totalSteps}</span>
          </div>
        )}
        {job.status === 'failed' && job.error && (
          <p className="text-[10px] text-danger/70 mt-0.5 truncate">{job.error}</p>
        )}
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <span className="text-[10px] text-white/20">{relativeTime(job.createdAt)}</span>
        {job.status === 'failed' && (
          <button onClick={onRetry} className="flex items-center gap-1 text-[10px] text-primary-400 hover:text-primary-300 transition-colors">
            <RotateCcw size={11} /> Retry
          </button>
        )}
      </div>
    </div>
  );
}
